import { useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useCookieConsent } from "@/hooks/use-cookie-consent";
import {
  enableSessionTelemetry,
  initPostHog,
  isAnalyticsExemptPath,
  isPublicMarketingPath,
} from "@/lib/posthog";

/**
 * Boots the PostHog client for the whole app.
 *
 * Two things keep it off the critical path. The SDK is imported dynamically, so
 * it never lands in the entry chunk; and the boot itself waits for the browser
 * to go idle after load, so analytics can never compete with the page's own
 * paint. Nothing renders here — there is no context to provide, so a wrapper
 * component would only be another React node to hydrate.
 *
 * This sits in `__root`, so it covers EVERY route — including the no-login
 * client review portal, whose URL is itself a capability token. The portal is
 * excluded here as well as in `initPostHog`, so neither a direct load nor an
 * in-app navigation mounts analytics over a client's review session.
 */
export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isExempt = isAnalyticsExemptPath(pathname);
  // `initPostHog` refuses without consent, so this effect exists to re-run the
  // boot the moment consent is granted. Without the dependency, someone who
  // accepts on the banner gets no analytics until their next page load.
  const analyticsAllowed = useCookieConsent().analytics;

  useEffect(() => {
    if (isExempt || !analyticsAllowed) {
      return;
    }
    return whenIdle(() => {
      void initPostHog();
    });
  }, [analyticsAllowed, isExempt]);

  // A visitor who landed on the marketing site and has now navigated into the
  // app: the lean init skipped session replay, so turn it on in place.
  useEffect(() => {
    if (isExempt || !analyticsAllowed || isPublicMarketingPath(pathname)) {
      return;
    }
    enableSessionTelemetry();
  }, [analyticsAllowed, isExempt, pathname]);

  return <>{children}</>;
}

/**
 * Run `task` once the browser has nothing better to do, and never before the
 * page has finished loading. Returns a cleanup that cancels a task that has not
 * run yet.
 */
function whenIdle(task: () => void) {
  let idleHandle: number | undefined;

  const schedule = () => {
    idleHandle = window.requestIdleCallback
      ? window.requestIdleCallback(task, { timeout: 4000 })
      : window.setTimeout(task, 1000);
  };

  if (document.readyState === "complete") {
    schedule();
    return () => cancelIdle(idleHandle);
  }

  window.addEventListener("load", schedule, { once: true });
  return () => {
    window.removeEventListener("load", schedule);
    cancelIdle(idleHandle);
  };
}

function cancelIdle(handle: number | undefined) {
  if (handle === undefined) {
    return;
  }
  if (window.cancelIdleCallback) {
    window.cancelIdleCallback(handle);
    return;
  }
  window.clearTimeout(handle);
}
